import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import EditRoomModal from './EditRoomModal';
import styles from '../assets/manageRoomsModal.module.css';

const ManageRoomsModal = ({ isOpen, onClose, propertyId, propertyName, onRoomsChange }) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);

  // Load rooms data
  const loadRooms = useCallback(async () => {
    if (!propertyId) return;
    
    setLoading(true);
    try {
        const BASE_URL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${BASE_URL}/api/rooms/properties/${propertyId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        console.log('🔍 [FRONTEND] Rooms loaded from API:', result.data);
        console.log('🔍 [FRONTEND] Room guardian data check:', result.data?.map(room => ({ 
          id: room.id, 
          name: room.name, 
          guardian: room.guardian,
          guardianType: typeof room.guardian 
        })));
        setRooms(result.data || []);
      } else {
        // Fallback to mock data
        setRooms([
          {
            id: 1,
            name: "Test Safi Saqlain",
            status: "VACANT",
            guardian: null,
            key_number: 2,
            price: 100,
            head_guardian: true,
            type_id: 1
          },
          {
            id: 2,
            name: "Room 102",
            status: "OCCUPIED",
            guardian: { unique_guardian_number: "UGD001", fullName: "John Smith" },
            key_number: 3,
            price: 150,
            head_guardian: false,
            type_id: 1
          }
        ]);
      }
    } catch (error) {
      console.error('Error loading rooms:', error);
      // Fallback to mock data
      setRooms([
        {
          id: 1,
          name: "Test Safi Saqlain",
          status: "VACANT",
          guardian: null,
          key_number: 2,
          price: 100,
          head_guardian: true,
          type_id: 1
        }
      ]);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    if (isOpen) {
      loadRooms();
    }
  }, [isOpen, propertyId, loadRooms]);

  // Handle head guardian checkbox change
  const handleHeadGuardianChange = async (roomId, checked) => {
    console.log('🔍 [FRONTEND] handleHeadGuardianChange called with:', { 
      roomId, 
      checked, 
      type: typeof roomId,
      isNaN: isNaN(roomId),
      parsed: parseInt(roomId)
    });
    
    // EMERGENCY FIX - More aggressive validation
    const numericRoomId = parseInt(roomId);
    if (!roomId || isNaN(numericRoomId) || numericRoomId <= 0) {
      console.error('❌ [FRONTEND] Invalid room ID:', roomId, 'Parsed:', numericRoomId);
      toast.error('Invalid room ID - cannot update');
      return;
    }
    
    console.log('✅ [FRONTEND] Using validated room ID:', numericRoomId);
    
    try {
      const BASE_URL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${BASE_URL}/api/rooms/${numericRoomId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ head_guardian: checked })
      });

      if (response.ok) {
        setRooms(rooms.map(room => 
          room.id === numericRoomId ? { ...room, head_guardian: checked } : room
        ));
        toast.success('Head Guardian status updated');
      } else {
        // EMERGENCY FIX - Better error handling
        const errorData = await response.json();
        console.error('❌ [FRONTEND] API Error:', response.status, errorData);
        toast.error(`Failed to update: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating head guardian:', error);
      toast.error('Failed to update Head Guardian status');
    }
  };

  // Handle edit room
  const handleEditRoom = (room) => {
    setSelectedRoom(room);
    setShowEditModal(true);
  };

  // Handle remove room
  const handleRemoveRoom = async (roomId) => {
    if (!window.confirm('Are you sure you want to remove this room?')) {
      return;
    }

    try {
      const BASE_URL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${BASE_URL}/api/rooms/${roomId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setRooms(rooms.filter(room => room.id !== roomId));
        toast.success(result.message || 'Room removed successfully');
        // Notify parent to refresh Current Vacancies section
        if (onRoomsChange) {
          onRoomsChange();
        }
      } else {
        // Display specific error message from API
        toast.error(result.message || 'Failed to remove room');
      }
    } catch (error) {
      console.error('Error removing room:', error);
      toast.error('Failed to remove room. Please try again.');
    }
  };

  // Handle edit modal close
  const handleEditModalClose = () => {
    setShowEditModal(false);
    setSelectedRoom(null);
    loadRooms(); // Refresh rooms data
    // Notify parent to refresh Current Vacancies section
    if (onRoomsChange) {
      onRoomsChange();
    }
  };

  // DRAG & DROP FUNCTIONS
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggedItem === null) return;

    const newRooms = [...rooms];
    const draggedRoom = newRooms[draggedItem];
    newRooms.splice(draggedItem, 1);
    newRooms.splice(dropIndex, 0, draggedRoom);

    setRooms(newRooms);
    setDraggedItem(null);

    // Save the new order to the backend (matching Laravel API)
    try {
      const BASE_URL = process.env.REACT_APP_API_URL;

      // Build order array matching Laravel format
      const order = newRooms.map((room, index) => ({
        roomId: room.id,
        position: index + 1
      }));

      const response = await fetch(`${BASE_URL}/api/rooms/sort`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          property_id: propertyId,
          order: order
        })
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Room order saved');
        // Notify parent to refresh Current Vacancies section
        if (onRoomsChange) {
          onRoomsChange();
        }
      } else {
        toast.error(result.message || 'Failed to save room order');
        // Reload rooms to get the actual order from database
        loadRooms();
      }
    } catch (error) {
      console.error('Error saving room order:', error);
      toast.error('Failed to save room order');
      // Reload rooms to get the actual order from database
      loadRooms();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={styles['modal-overlay']} onClick={onClose}>
        <div className={styles['manage-rooms-modal']} onClick={(e) => e.stopPropagation()}>
          {/* Modal Header */}
          <div className={styles['modal-header']}>
            <h4 className={styles['modal-title']}>Manage rooms</h4>
            <button 
              type="button" 
              className={styles['btn-modal-close']} 
              onClick={onClose}
            >
              &times;
            </button>
          </div>

          {/* Modal Body */}
          <div className={styles['modal-body']}>
            <div style={{ visibility: 'hidden' }} id="property_id" data-property-id={propertyId}></div>
            
            {loading ? (
              <div className="text-center">
                <div className={styles['spinner-border']}></div>
                <p>Loading rooms...</p>
              </div>
            ) : (
              <table className={styles['table']}>
                <thead>
                  <tr>
                    <th></th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Assigned Guardian</th>
                    <th>Key</th>
                    <th>Price</th>
                    <th>HG</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody id="sortable">
                  {rooms.map((room, index) => {
                    // EMERGENCY FIX - Safe data extraction to prevent object rendering
                    const safeRoomName = (() => {
                      if (typeof room.name === 'string') return room.name;
                      if (typeof room.name === 'object' && room.name?.name) return room.name.name;
                      if (typeof room.name === 'object') return 'Room Object';
                      return 'Unknown';
                    })();
                    
                    const safeStatus = (() => {
                      if (typeof room.status === 'string') return room.status;
                      if (typeof room.status === 'object' && room.status?.name) return room.status.name;
                      if (typeof room.status === 'object') return 'Status Object';
                      return 'Unknown';
                    })();
                    
                    const safeGuardian = (() => {
                      if (!room.guardian) return 'Empty';
                      if (typeof room.guardian === 'string') return room.guardian;
                      if (typeof room.guardian === 'object') {
                        if (room.guardian.fullName) return room.guardian.fullName;
                        if (room.guardian.name) return room.guardian.name;
                        if (room.guardian.unique_guardian_number) return room.guardian.unique_guardian_number;
                        return 'Guardian Object';
                      }
                      return 'Unknown';
                    })();
                    
                    const safeKeyNumber = (() => {
                      if (typeof room.key_number === 'number') return room.key_number.toString();
                      if (typeof room.key_number === 'string') return room.key_number;
                      if (typeof room.key_number === 'object' && room.key_number?.key_number) return room.key_number.key_number.toString();
                      return '0';
                    })();
                    
                    const safePrice = (() => {
                      if (typeof room.price === 'number') return room.price.toString();
                      if (typeof room.price === 'string') return room.price;
                      if (typeof room.price === 'object' && room.price?.price) return room.price.price.toString();
                      return '0';
                    })();
                    
                    console.log('🔍 [FRONTEND] Safe room data:', { 
                      id: room.id, 
                      name: safeRoomName,
                      status: safeStatus,
                      guardian: safeGuardian,
                      keyNumber: safeKeyNumber,
                      price: safePrice
                    });
                    
                    return (
                    <tr 
                      key={room.id || `room-${index}`} 
                      data-id={room.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index)}
                      style={{ cursor: 'move' }}
                    >
                      <td>
                        <img src="/assets/images/icon-sortable.png" alt="Sort" />
                      </td>
                      <td>{safeRoomName}</td>
                      <td>
                        <span className={`${styles['room-' + safeStatus.toLowerCase().replace(' ', '-')]}`}>
                          {safeStatus}
                        </span>
                      </td>
                      <td>{safeGuardian === 'Empty' ? '-' : safeGuardian}</td>
                      <td>{safeKeyNumber}</td>
                      <td>£{safePrice}</td>
                      <td>
                        <input
                          type="checkbox"
                          name="head_guardian"
                          id="head_guardian"
                          className={styles['js-select-head-guardian']}
                          data-room={room.id}
                          checked={room.head_guardian}
                          onChange={(e) => {
                            console.log('🔍 [FRONTEND] Checkbox clicked for room:', { 
                              id: room.id, 
                              type: typeof room.id, 
                              isNaN: isNaN(room.id),
                              name: room.name 
                            });
                            handleHeadGuardianChange(room.id, e.target.checked);
                          }}
                        />
                      </td>
                      <td>
                        <button
                          className={`${styles['btn']} ${styles['btn-sm']} ${styles['btn-info']}`}
                          onClick={() => handleEditRoom(room)}
                        >
                          Edit
                        </button>
                        {(!room.guardian || (typeof room.guardian === 'object' && !room.guardian.fullName)) && (
                          <button
                            className={`${styles['btn']} ${styles['btn-sm']} ${styles['btn-danger']}`}
                            onClick={() => handleRemoveRoom(room.id)}
                            data-confirm-action="Are you sure you want to remove?"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Modal Footer */}
          <div className={styles['modal-footer']}>
            <button 
              type="button" 
              className={`${styles['btn-default']} ${styles['btn-modal-close']}`} 
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Edit Room Modal */}
      {showEditModal && selectedRoom && (
        <EditRoomModal
          isOpen={showEditModal}
          onClose={handleEditModalClose}
          room={selectedRoom}
          propertyId={propertyId}
        />
      )}
    </>
  );
};

export default ManageRoomsModal;
